import {
  FileExcelOutlined,
  FilePdfOutlined,
  LoadingOutlined,
  PrinterOutlined,
} from '@ant-design/icons'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { Dropdown, MenuProps, Modal } from 'antd'
import { useState } from 'react'
import { toast } from 'sonner'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import type { InvoiceData } from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import { exportInvoiceToExcel } from '../excelExport'
import type { InvoiceType } from '../invoiceUtils'
import { PdfInvoiceTemplate } from '../OrderInvoicePDF/OrderInvoicePDF'
import cn from './OrderExportButtons.module.scss'

export const OrderExportButtons = ({ orderCode }: { orderCode: number }) => {
  const [clientInvoiceData, setClientInvoiceData] =
    useState<InvoiceData | null>(null)
  const [rrcInvoiceData, setRrcInvoiceData] = useState<InvoiceData | null>(null)
  const [clientLoading, setClientLoading] = useState(false)
  const [rrcLoading, setRrcLoading] = useState(false)
  const [pdfIssuedAt, setPdfIssuedAt] = useState(() => new Date())

  const [modal] = Modal.useModal()

  const fetchClientInvoiceData = async () => {
    if (clientInvoiceData || clientLoading) return
    setClientLoading(true)
    try {
      const res = await api.get(`/orders/${orderCode}/invoice-data`)
      const actualData = res.data.invoiceData || res.data
      setClientInvoiceData(actualData)
    } catch (e) {
      handleApiError(e, modal)
    } finally {
      setClientLoading(false)
    }
  }

  const fetchRrcInvoiceData = async () => {
    if (rrcInvoiceData || rrcLoading) return
    setRrcLoading(true)
    try {
      const res = await api.get(`/orders/${orderCode}/client-invoice-data`)
      const actualData = res.data.invoiceData || res.data
      setRrcInvoiceData(actualData)
    } catch (e) {
      handleApiError(e, modal)
    } finally {
      setRrcLoading(false)
    }
  }

  const fetchAllInvoiceData = () => {
    fetchClientInvoiceData()
    fetchRrcInvoiceData()
  }

  const handleExcelClick = async (type: InvoiceType) => {
    if (type === 'CLIENT') {
      if (!clientInvoiceData) await fetchClientInvoiceData()
      if (clientInvoiceData) {
        await exportInvoiceToExcel(clientInvoiceData, type)
      } else {
        toast.warning('Данные еще загружаются, попробуйте через секунду')
      }
    } else {
      if (!rrcInvoiceData) await fetchRrcInvoiceData()
      if (rrcInvoiceData) {
        await exportInvoiceToExcel(rrcInvoiceData, type)
      } else {
        toast.warning('Данные еще загружаются, попробуйте через секунду')
      }
    }
  }

  const items: MenuProps['items'] = [
    {
      key: 'pdf',
      label: 'Печать в PDF',
      icon: <FilePdfOutlined />,
      onMouseEnter: fetchAllInvoiceData,
      children: [
        {
          key: 'pdf-client',
          label: clientInvoiceData ? (
            <PDFDownloadLink
              document={
                <PdfInvoiceTemplate
                  data={clientInvoiceData}
                  type="CLIENT"
                  issuedAt={pdfIssuedAt}
                />
              }
              fileName={`Счет на оплату клиенту №${orderCode}.pdf`}
              className={cn.downloadLink}
            >
              {({ loading: pdfLoading }) =>
                pdfLoading ? 'Генерация...' : 'Счет клиенту (ОПТ)'
              }
            </PDFDownloadLink>
          ) : (
            <span>
              {clientLoading ? 'Загрузка данных...' : 'Наведите для подготовки'}
            </span>
          ),
          disabled: !clientInvoiceData,
        },
        {
          key: 'pdf-rrc',
          label: rrcInvoiceData ? (
            <PDFDownloadLink
              document={
                <PdfInvoiceTemplate
                  data={rrcInvoiceData}
                  type="RRC"
                  issuedAt={pdfIssuedAt}
                />
              }
              fileName={`Счет на оплату РРЦ №${orderCode}.pdf`}
              className={cn.downloadLink}
            >
              {({ loading: pdfLoading }) =>
                pdfLoading ? 'Генерация...' : 'Счет РРЦ'
              }
            </PDFDownloadLink>
          ) : (
            <span className={cn.pendingLabel}>
              {rrcLoading ? <LoadingOutlined /> : 'Загрузка...'}
            </span>
          ),
          disabled: !rrcInvoiceData,
        },
        {
          key: 'pdf-rrc-hidden',
          label: rrcInvoiceData ? (
            <PDFDownloadLink
              document={
                <PdfInvoiceTemplate
                  data={rrcInvoiceData}
                  type="RRC_HIDDEN"
                  issuedAt={pdfIssuedAt}
                />
              }
              fileName={`Счет на оплату РРЦ (Скрытый) №${orderCode}.pdf`}
              className={cn.downloadLink}
            >
              {({ loading: pdfLoading }) =>
                pdfLoading ? 'Генерация...' : 'Счет РРЦ (Скрытый)'
              }
            </PDFDownloadLink>
          ) : (
            <span className={cn.pendingLabel}>
              {rrcLoading ? <LoadingOutlined /> : 'Загрузка...'}
            </span>
          ),
          disabled: !rrcInvoiceData,
        },
      ],
    },
    {
      key: 'excel',
      label: 'Экспорт в Excel',
      icon: <FileExcelOutlined />,
      onMouseEnter: fetchAllInvoiceData,
      children: [
        {
          key: 'xls-client',
          label: 'Счет клиенту (ОПТ)',
          onClick: () => handleExcelClick('CLIENT'),
        },
        {
          key: 'xls-rrc',
          label: 'Счет РРЦ',
          onClick: () => handleExcelClick('RRC'),
        },
        {
          key: 'xls-rrc-hidden',
          label: 'Счет РРЦ (Скрытый)',
          onClick: () => handleExcelClick('RRC_HIDDEN'),
        },
      ],
    },
  ]

  return (
    <Dropdown
      menu={{ items }}
      trigger={['hover']}
      placement="bottomLeft"
      destroyOnHidden={false}
      onOpenChange={(open) => {
        if (open) setPdfIssuedAt(new Date())
      }}
    >
      <span className={cn.trigger}>
        <AtomButton variant="secondary" icon={<PrinterOutlined />}>
          Печать счета
        </AtomButton>
      </span>
    </Dropdown>
  )
}
